import React, { useEffect, useState } from "react";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import AnimatedSection from "../components/AnimatedSection";
import LoadingScreen from "../components/LoadingScreen";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const categories = [
  "All",
  "Hotels & Restaurants",
  "Tea Factories & Industries",
  "Home & Living",
  "Others",
];

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/products`);
        setProducts(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const matchCategory = (pCategory, selected) => {
    return (
      pCategory &&
      pCategory.toLowerCase().trim() === selected.toLowerCase().trim()
    );
  };

  const groupedProducts =
    active === "All"
      ? categories.slice(1).map((cat) => ({
          category: cat,
          items: products.filter((p) => matchCategory(p.category, cat)),
        }))
      : [
          {
            category: active,
            items: products.filter((p) => matchCategory(p.category, active)),
          },
        ];

        if (loading) {
    return <LoadingScreen text="Loading products..." />;
  }

  return (
    <div className="overflow-x-hidden font-inter">
      {/* HERO */}
      <section className="bg-gradient-hero text-primary-foreground py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8">
          <AnimatedSection>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">
              Our Products
            </h1>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg opacity-80 max-w-xl">
              Premium steel products engineered for strength, durability, and
              precision.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="pt-8 pb-12 sm:pt-10 sm:pb-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          {/* FILTER BUTTONS */}
          <div className="flex gap-3 mb-6 sm:mb-8 overflow-x-auto scrollbar-hide py-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`flex-shrink-0 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm sm:text-sm font-medium transition-all duration-300 border transform ${
                  active === cat
                    ? "bg-gradient-hero text-white shadow-md scale-105"
                    : "bg-background text-primary hover:bg-gray-100 hover:shadow-sm hover:scale-105"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* LOADING */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-72 w-full bg-gray-200 rounded-2xl animate-pulse"
                />
              ))}
            </div>
          ) : (
            <>
              {/* GROUPED SECTIONS */}
              {groupedProducts.map(({ category, items }) =>
                items.length > 0 ? (
                  <div key={category} className="mb-16">
                    {/* CATEGORY TITLE */}
                    <AnimatedSection>
                      <div className="flex flex-row items-center gap-4 mb-6 sm:mb-10 flex-nowrap">
                        {/* Accent Line */}
                        <div className="w-1 h-10 bg-gradient-to-b from-green-400 to-green-600 rounded-full flex-shrink-0" />

                        {/* Title + Subtitle */}
                        <div className="min-w-0">
                          <h2 className="text-2xl md:text-3xl font-semibold text-green-800 tracking-tight whitespace-nowrap overflow-hidden text-ellipsis">
                            {category}
                          </h2>

                          <p className="text-sm text-gray-500 mt-1 whitespace-nowrap overflow-hidden text-ellipsis">
                            High-quality solutions tailored for{" "}
                            {category.toLowerCase()}
                          </p>
                        </div>
                      </div>
                    </AnimatedSection>

                    {/* PRODUCTS GRID */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {items.map((product, i) => (
                        <AnimatedSection key={product._id} delay={i * 0.05}>
                          <ProductCard product={product} />
                        </AnimatedSection>
                      ))}
                    </div>
                  </div>
                ) : null,
              )}

              {/* NO RESULTS */}
              {groupedProducts.every((g) => g.items.length === 0) && (
                <p className="text-center text-muted-foreground">
                  No products found
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
